import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Space, Switch, Tooltip } from 'antd';
import { ScopeCheckbox } from 'components/Custom/checkbox';
import { MessageStates } from 'containers/MessageBox';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import styled, { createGlobalStyle } from 'styled-components';

const Wrapper = styled.div`
  background: var(--color-layout-bg, #e9f6f7);
  font-family: Inter, sans-serif;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100vh - 110px);
  .top-section {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .bottom-section {
    flex-shrink: 0;
    padding: 16px 0;
    margin-top: auto;
  }
`;

const ColumnListWrapper = styled.div<{ deduct: number }>`
  max-height: calc(100vh - ${({ deduct }) => deduct}px);
  min-height: 45px;
  overflow-y: auto;
  background: var(--color-surface-raised, #fff);
  border: 1px solid var(--color-border, #dbeae3);
  padding: 0 8px 12px 8px;
  margin-bottom: 15px;
  /* Thinner, modern scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border, #dbeae3);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const ColumnItem = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  user-select: none;
  padding: 6px 0;
  border-bottom: 1px solid #e8f0f0;
  &:last-child {
    margin-bottom: 0;
    border-bottom: none;
  }
  &:has(input[type='checkbox']:disabled) {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ColumnItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const ColumnItemRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ColumnText = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
  color: var(--color-text-dark, #042a2a);
`;

const CustomCheckboxSizeStyle = createGlobalStyle`
  /* Only remove bottom padding when this specific form is in the drawer */
  .ant-drawer-body:has(.column-customization-wrapper) {
    padding-bottom: 0 !important;
  }

  /*
   * Entity Settings drawer: Switch sits outside StyledCard/ScopeTable scope, so theme
   * rules for the switch-checked classes / handle never apply — force visible on/off styles.
   */
  .column-customization-wrapper .transactions-settings-drawer-switch.ant-switch-checked {
    background: var(--color-primary, #41aaaf) !important;
  }
  .column-customization-wrapper
    .transactions-settings-drawer-switch.ant-switch:not(.ant-switch-checked) {
    background: #c5d5d5 !important;
  }
  .column-customization-wrapper .transactions-settings-drawer-switch.ant-switch-handle::before {
    background: var(--color-surface-raised, #ffffff) !important;
  }
`;

interface ColumnOption {
  key: string;
  title: string;
  fixed?: boolean | 'left' | 'right';
  alwaysFixed?: boolean;
}

interface ColumnCustomizationDrawerProps {
  columns: ColumnOption[];
  visibleColumns: string[];
  lockedColumns?: string[];
  columnOrder?: string[];
  alwaysFixedColumns?: string[];
  excludedColumns?: string[]; // Columns that won't appear in customization (always shown & fixed)
  /** Column keys that are optional - show "(optional)" in light grey next to the label */
  optionalColumnKeys?: string[];
  /** Column keys shown in the list but cannot be moved (e.g. fixed tail amount block). */
  nonReorderableColumnKeys?: string[];
  /**
   * Always shown in the table: no visibility checkbox (cannot be hidden),
   * but still listed so the user can lock/unlock. Combined with
   * `nonReorderableColumnKeys` for qty / unit price / discount.
   */
  alwaysVisibleColumnKeys?: string[];
  /** Rendered above the column list (e.g. entity transactions “edit in table” toggle). */
  topSection?: React.ReactNode;
  /** Small heading above `topSection` (e.g. Settings drawer — first block). */
  topSectionHeading?: string;
  /** One-line description under the top heading. */
  topSectionDescription?: string;
  /** Small heading above the column list (e.g. Settings drawer — second block). */
  columnListHeading?: string;
  /** One-line description under the column heading. */
  columnListDescription?: string;
  /** Label for the primary save button (default: Save Columns). */
  saveButtonLabel?: string;
  onSave?: (config: {
    visibleColumns: string[];
    lockedColumns: string[];
    columnOrder: string[];
  }) => void;
  /**
   * When set, replaces the single save button with two (e.g. this entity vs all entities).
   * `onSave` is ignored — use both callbacks instead.
   */
  dualEntityScopeSave?: {
    saveForEntityLabel: string;
    saveForAllEntitiesLabel: string;
    onSaveForEntity: (config: {
      visibleColumns: string[];
      lockedColumns: string[];
      columnOrder: string[];
    }) => void;
    onSaveForAllEntities: (config: {
      visibleColumns: string[];
      lockedColumns: string[];
      columnOrder: string[];
    }) => void;
    successDescriptionForEntity: string;
    successDescriptionForAllEntities: string;
  };
  /** Merged into change detection so e.g. a draft toggle above the columns list enables Save. */
  additionalHasChanges?: boolean;
  onClose?: () => void;
}

export const ColumnCustomizationDrawer: React.FC<
  ColumnCustomizationDrawerProps
> = ({
  columns,
  visibleColumns,
  lockedColumns = [],
  columnOrder,
  alwaysFixedColumns = [],
  excludedColumns = ['actions'], // Columns excluded from customization UI
  optionalColumnKeys = [],
  nonReorderableColumnKeys = [],
  alwaysVisibleColumnKeys = [],
  topSection,
  topSectionHeading,
  topSectionDescription,
  columnListHeading,
  columnListDescription,
  saveButtonLabel,
  onSave,
  dualEntityScopeSave,
  additionalHasChanges = false,
  onClose,
}) => {
  const { t } = useTranslation();
  const resolvedSaveLabel = saveButtonLabel ?? t('common.save-columns');
  const dispatch = useDispatch();
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [lockedCols, setLockedCols] = useState<string[]>([]);
  const [orderedColumns, setOrderedColumns] = useState<ColumnOption[]>([]);

  // Use refs to track previous values for deep comparison
  const prevPropsRef = useRef<{
    visibleColumns: string[];
    lockedColumns: string[];
    columnOrder?: string[];
    columns: ColumnOption[];
    alwaysFixedColumns: string[];
    excludedColumns: string[];
    nonReorderableColumnKeys: string[];
    alwaysVisibleColumnKeys: string[];
  }>({
    visibleColumns: [],
    lockedColumns: [],
    columnOrder: undefined,
    columns: [],
    alwaysFixedColumns: [],
    excludedColumns: [],
    nonReorderableColumnKeys: [],
    alwaysVisibleColumnKeys: [],
  });

  useEffect(() => {
    // Deep comparison helper
    const arraysEqual = (a: string[], b: string[]) => {
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return sortedA.every((val, idx) => val === sortedB[idx]);
    };

    const columnsEqual = (a: ColumnOption[], b: ColumnOption[]) => {
      if (a.length !== b.length) return false;
      return a.every((col, idx) => {
        const otherCol = b[idx];
        return (
          col.key === otherCol.key &&
          col.title === otherCol.title &&
          col.fixed === otherCol.fixed &&
          col.alwaysFixed === otherCol.alwaysFixed
        );
      });
    };

    const prev = prevPropsRef.current;

    // Check if any dependencies actually changed
    const visibleChanged = !arraysEqual(visibleColumns, prev.visibleColumns);
    const lockedChanged = !arraysEqual(lockedColumns, prev.lockedColumns);
    const orderChanged =
      JSON.stringify(columnOrder || []) !==
      JSON.stringify(prev.columnOrder || []);
    const columnsChanged = !columnsEqual(columns, prev.columns);
    const alwaysFixedChanged = !arraysEqual(
      alwaysFixedColumns,
      prev.alwaysFixedColumns
    );
    const excludedChanged = !arraysEqual(excludedColumns, prev.excludedColumns);
    const nonReorderableChanged = !arraysEqual(
      nonReorderableColumnKeys,
      prev.nonReorderableColumnKeys ?? []
    );
    const alwaysVisibleChanged = !arraysEqual(
      alwaysVisibleColumnKeys,
      prev.alwaysVisibleColumnKeys ?? []
    );

    // Only update if something actually changed
    if (
      !visibleChanged &&
      !lockedChanged &&
      !orderChanged &&
      !columnsChanged &&
      !alwaysFixedChanged &&
      !excludedChanged &&
      !nonReorderableChanged &&
      !alwaysVisibleChanged
    ) {
      return;
    }

    // Update refs with current values
    prevPropsRef.current = {
      visibleColumns: [...visibleColumns],
      lockedColumns: [...lockedColumns],
      columnOrder: columnOrder ? [...columnOrder] : undefined,
      columns: columns.map((col) => ({ ...col })),
      alwaysFixedColumns: [...alwaysFixedColumns],
      excludedColumns: [...excludedColumns],
      nonReorderableColumnKeys: [...nonReorderableColumnKeys],
      alwaysVisibleColumnKeys: [...alwaysVisibleColumnKeys],
    };

    // Only update state if values actually changed
    if (visibleChanged || alwaysVisibleChanged) {
      const merged = [...visibleColumns];
      for (const key of alwaysVisibleColumnKeys) {
        if (!merged.includes(key)) merged.push(key);
      }
      setSelectedColumns(merged);
    }
    if (lockedChanged) {
      setLockedCols([...lockedColumns]);
    }

    // Filter out always-fixed columns and excluded columns from customizable list
    const customizableCols = columns.filter(
      (col) =>
        !col.fixed &&
        !alwaysFixedColumns.includes(col.key) &&
        !excludedColumns.includes(col.key)
    );

    // Only update orderedColumns if columns or order changed
    if (
      columnsChanged ||
      orderChanged ||
      alwaysFixedChanged ||
      excludedChanged
    ) {
      // Ensure Entity is first in the list when present (for transactions/exceptions tables)
      const withEntityFirst = (cols: ColumnOption[]) => {
        const entityCol = cols.find((col) => col.key === 'entity');
        if (!entityCol) return cols;
        return [entityCol, ...cols.filter((col) => col.key !== 'entity')];
      };

      const withTailBlockLast = (cols: ColumnOption[]) => {
        if (!nonReorderableColumnKeys.length) return cols;
        const tailSet = new Set(nonReorderableColumnKeys);
        const head = cols.filter((col) => !tailSet.has(col.key));
        const tail = nonReorderableColumnKeys
          .map((key) => cols.find((col) => col.key === key))
          .filter((col): col is ColumnOption => col != null);
        return [...head, ...tail];
      };

      // Initialize column order
      if (columnOrder && columnOrder.length > 0) {
        const ordered = columnOrder
          .map((key) => customizableCols.find((col) => col.key === key))
          .filter((col): col is ColumnOption => col !== undefined);
        // Add any columns not in the order at the end
        const unordered = customizableCols.filter(
          (col) => !columnOrder.includes(col.key)
        );
        setOrderedColumns(
          withEntityFirst(withTailBlockLast([...ordered, ...unordered]))
        );
      } else {
        // Default order: all customizable columns (Entity first when present)
        setOrderedColumns(withEntityFirst(withTailBlockLast(customizableCols)));
      }
    }
  }, [
    visibleColumns,
    lockedColumns,
    columnOrder,
    columns,
    alwaysFixedColumns,
    excludedColumns,
    nonReorderableColumnKeys,
    alwaysVisibleColumnKeys,
  ]);

  const fixedColumns = columns.filter((col) => col.fixed);
  const customizableColumns =
    orderedColumns.length > 0
      ? orderedColumns
      : columns.filter(
          (col) =>
            !col.fixed &&
            !alwaysFixedColumns.includes(col.key) &&
            !excludedColumns.includes(col.key)
        );

  const isAlwaysVisible = (columnKey: string) =>
    alwaysVisibleColumnKeys.includes(columnKey);

  const handleToggle = (columnKey: string, checked: boolean) => {
    if (isAlwaysVisible(columnKey)) return;
    if (checked) {
      setSelectedColumns([...selectedColumns, columnKey]);
    } else {
      setSelectedColumns(selectedColumns.filter((key) => key !== columnKey));
      // Remove lock when column is unchecked
      setLockedCols(lockedCols.filter((key) => key !== columnKey));
    }
  };

  const handleLockToggle = (columnKey: string, locked: boolean) => {
    if (locked) {
      // Check if we already have 2 locked columns (excluding always-fixed ones)
      const userLockedCount = lockedCols.filter(
        (key) => !alwaysFixedColumns.includes(key)
      ).length;
      if (userLockedCount >= 2) {
        // Don't allow locking more than 2 columns
        return;
      }
      setLockedCols([...lockedCols, columnKey]);
    } else {
      setLockedCols(lockedCols.filter((key) => key !== columnKey));
    }
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const column = orderedColumns[index];
    if (nonReorderableColumnKeys.includes(column.key)) return;
    const newOrder = [...orderedColumns];
    [newOrder[index - 1], newOrder[index]] = [
      newOrder[index],
      newOrder[index - 1],
    ];
    setOrderedColumns(newOrder);
  };

  const handleMoveDown = (index: number) => {
    if (index === orderedColumns.length - 1) return;
    const column = orderedColumns[index];
    if (nonReorderableColumnKeys.includes(column.key)) return;
    const next = orderedColumns[index + 1];
    if (nonReorderableColumnKeys.includes(next.key)) return;
    const newOrder = [...orderedColumns];
    [newOrder[index], newOrder[index + 1]] = [
      newOrder[index + 1],
      newOrder[index],
    ];
    setOrderedColumns(newOrder);
  };

  const buildSaveConfig = () => {
    const finalOrder = [
      ...fixedColumns
        .filter((col) => col.fixed === 'left')
        .map((col) => col.key),
      ...customizableColumns.map((col) => col.key),
      ...fixedColumns
        .filter((col) => col.fixed === 'right')
        .map((col) => col.key),
    ];
    const visible = [...selectedColumns];
    for (const key of alwaysVisibleColumnKeys) {
      if (!visible.includes(key)) visible.push(key);
    }
    return {
      visibleColumns: visible,
      lockedColumns: lockedCols,
      columnOrder: finalOrder,
    };
  };

  const showSaveSuccess = (description: string) => {
    const message: MessageData = {
      title: t('common.success-exclamation'),
      description,
      type: MessageStates.SUCCESS,
    };
    dispatch(turnMessageOn(message));
  };

  const handleSave = () => {
    const config = buildSaveConfig();
    onSave?.(config);
    showSaveSuccess(t('dashboard.column-preferences-saved'));
    onClose?.();
  };

  const handleSaveForEntity = () => {
    if (!dualEntityScopeSave) return;
    dualEntityScopeSave.onSaveForEntity(buildSaveConfig());
    showSaveSuccess(dualEntityScopeSave.successDescriptionForEntity);
    onClose?.();
  };

  const handleSaveForAllEntities = () => {
    if (!dualEntityScopeSave) return;
    dualEntityScopeSave.onSaveForAllEntities(buildSaveConfig());
    showSaveSuccess(dualEntityScopeSave.successDescriptionForAllEntities);
    onClose?.();
  };

  const isAlwaysFixed = (columnKey: string) =>
    alwaysFixedColumns.includes(columnKey);

  // Check if there are any changes
  const hasColumnOrOrderChanges =
    JSON.stringify([...selectedColumns].sort()) !==
      JSON.stringify([...visibleColumns].sort()) ||
    JSON.stringify([...lockedCols].sort()) !==
      JSON.stringify([...lockedColumns].sort()) ||
    JSON.stringify(customizableColumns.map((col) => col.key)) !==
      JSON.stringify(
        columnOrder
          ? columnOrder.filter(
              (key) => !fixedColumns.some((col) => col.key === key)
            )
          : customizableColumns.map((col) => col.key)
      );

  const hasChanges = hasColumnOrOrderChanges || Boolean(additionalHasChanges);

  return (
    <>
      <CustomCheckboxSizeStyle />
      <Wrapper className="column-customization-wrapper">
        <div className="top-section">
          {topSection != null || topSectionHeading || topSectionDescription ? (
            <div style={{ marginBottom: 0 }}>
              {topSectionHeading ? (
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--color-text-dark, #042a2a)',
                    lineHeight: '18px',
                    marginBottom: topSectionDescription ? 4 : 8,
                  }}
                >
                  {topSectionHeading}
                </div>
              ) : null}
              {topSectionDescription ? (
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: 'var(--color-text-muted, #5a6a6a)',
                    lineHeight: '16px',
                    marginBottom: 12,
                  }}
                >
                  {topSectionDescription}
                </div>
              ) : null}
              {topSection != null ? (
                <div style={{ marginBottom: 0 }}>{topSection}</div>
              ) : null}
            </div>
          ) : null}
          {(topSection != null || topSectionHeading || topSectionDescription) &&
          customizableColumns.length > 0 ? (
            <div
              role="separator"
              style={{
                borderTop: '1px solid var(--color-border, #dbeae3)',
                margin: '16px 0',
              }}
            />
          ) : null}
          {columnListHeading || columnListDescription ? (
            <div style={{ marginBottom: 10 }}>
              {columnListHeading ? (
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--color-text-dark, #042a2a)',
                    lineHeight: '18px',
                    marginBottom: columnListDescription ? 4 : 8,
                  }}
                >
                  {columnListHeading}
                </div>
              ) : null}
              {columnListDescription ? (
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: 'var(--color-text-muted, #5a6a6a)',
                    lineHeight: '16px',
                    marginBottom: 12,
                  }}
                >
                  {columnListDescription}
                </div>
              ) : null}
            </div>
          ) : null}
          <ColumnListWrapper
            deduct={180}
            tabIndex={0}
            role="region"
            aria-label={columnListHeading || t('main.columns')}
          >
            <div
              style={{
                paddingTop: columnListHeading || columnListDescription ? 0 : 12,
              }}
            >
              {customizableColumns.map((column, index) => {
                const alwaysVisible = isAlwaysVisible(column.key);
                const isVisible =
                  alwaysVisible || selectedColumns.includes(column.key);
                const isLocked =
                  lockedCols.includes(column.key) || isAlwaysFixed(column.key);
                const canLock = !isAlwaysFixed(column.key) && isVisible;
                const userLockedCount = lockedCols.filter(
                  (key) => !alwaysFixedColumns.includes(key)
                ).length;
                const canLockMore = userLockedCount < 2 || isLocked;
                const isNonReorderable = nonReorderableColumnKeys.includes(
                  column.key
                );
                const reorderableCount = customizableColumns.filter(
                  (col) => !nonReorderableColumnKeys.includes(col.key)
                ).length;
                const canMoveUp = !isNonReorderable && index > 0;
                const canMoveDown =
                  !isNonReorderable && index < reorderableCount - 1;

                return (
                  <ColumnItem
                    key={column.key}
                    style={alwaysVisible ? { cursor: 'default' } : undefined}
                  >
                    <ColumnItemLeft>
                      {alwaysVisible ? (
                        <span
                          aria-hidden
                          style={{
                            width: 16,
                            marginRight: 8,
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <Tooltip
                          title={
                            isVisible
                              ? t('common.visible-uncheck-to-hide')
                              : t('common.hidden-check-to-show')
                          }
                        >
                          <ScopeCheckbox
                            compact
                            checked={isVisible}
                            onChange={(e) =>
                              handleToggle(column.key, e.target.checked)
                            }
                            disabled={isAlwaysFixed(column.key)}
                            style={{ marginRight: 8 }}
                          />
                        </Tooltip>
                      )}
                      <ColumnText>
                        {column.title || column.key}
                        {optionalColumnKeys.includes(column.key) && (
                          <span
                            style={{
                              marginLeft: 6,
                              fontSize: 11,
                              color: '#8c8c8c',
                              fontWeight: 400,
                            }}
                          >
                            (optional)
                          </span>
                        )}
                      </ColumnText>
                    </ColumnItemLeft>
                    <ColumnItemRight>
                      {!isNonReorderable ? (
                        <>
                          <Tooltip title={t('other.move-column-up')}>
                            <ArrowUpOutlined
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (canMoveUp) handleMoveUp(index);
                              }}
                              style={{
                                cursor: canMoveUp ? 'pointer' : 'not-allowed',
                                color: canMoveUp
                                  ? 'var(--color-text-muted, #5a6a6a)'
                                  : '#d9d9d9',
                                fontSize: 12,
                              }}
                            />
                          </Tooltip>
                          <Tooltip title={t('other.move-column-down')}>
                            <ArrowDownOutlined
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (canMoveDown) handleMoveDown(index);
                              }}
                              style={{
                                cursor: canMoveDown ? 'pointer' : 'not-allowed',
                                color: canMoveDown
                                  ? 'var(--color-text-muted, #5a6a6a)'
                                  : '#d9d9d9',
                                fontSize: 12,
                              }}
                            />
                          </Tooltip>
                        </>
                      ) : null}
                      <div
                        style={{
                          width: 1,
                          height: 16,
                          backgroundColor: 'var(--color-border, #dbeae3)',
                          margin: '0 8px',
                        }}
                      />
                      <Tooltip
                        title={
                          !isVisible
                            ? t('organisation.column-lock-select-to-enable')
                            : !canLock
                            ? t('organisation.column-lock-cannot-lock')
                            : isLocked
                            ? t('organisation.column-lock-unlock-to-reorder')
                            : userLockedCount >= 2
                            ? t('organisation.column-lock-max-locked')
                            : t('organisation.column-lock-lock-to-fix')
                        }
                      >
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <LockOutlined
                            style={{
                              color: isLocked ? 'var(--color-primary, #41aaaf)' : 'var(--color-text-muted, #5a6a6a)',
                              fontSize: 12,
                            }}
                          />
                          <Switch
                            size="small"
                            checked={isLocked}
                            aria-label={`${column.title || column.key}: ${
                              isLocked
                                ? t('organisation.column-lock-unlock-to-reorder')
                                : t('organisation.column-lock-lock-to-fix')
                            }`}
                            onChange={(checked) =>
                              handleLockToggle(column.key, checked)
                            }
                            disabled={!canLock || (!isLocked && !canLockMore)}
                            style={{ minWidth: 40 }}
                          />
                        </span>
                      </Tooltip>
                    </ColumnItemRight>
                  </ColumnItem>
                );
              })}
            </div>
          </ColumnListWrapper>
        </div>
        <div className="bottom-section">
          {dualEntityScopeSave ? (
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              <ScopeButton
                type="primary"
                width="100%"
                height="45px"
                disabled={!hasChanges}
                onClick={handleSaveForEntity}
              >
                {dualEntityScopeSave.saveForEntityLabel}
              </ScopeButton>
              <ScopeButton
                type="default"
                width="100%"
                height="45px"
                disabled={!hasChanges}
                onClick={handleSaveForAllEntities}
                style={{
                  borderColor: 'var(--color-border, #dbeae3)',
                  color: 'var(--color-text-dark, #042a2a)',
                  fontWeight: 600,
                }}
              >
                {dualEntityScopeSave.saveForAllEntitiesLabel}
              </ScopeButton>
            </Space>
          ) : (
            <ScopeButton
              type="primary"
              width="100%"
              height="45px"
              disabled={!hasChanges}
              onClick={handleSave}
            >
              {resolvedSaveLabel}
            </ScopeButton>
          )}
        </div>
      </Wrapper>
    </>
  );
};
